import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wrench, Lightning, MapPin, User, Phone, Envelope, ArrowRight, ArrowLeft,
  CheckCircle, Star, Briefcase, Clock, Certificate, CaretDown, Plus, X,
  Hammer, HardHat, PaintBrush, Plug, Drop, Thermometer, TreeEvergreen,
  CurrencyDollar, CalendarBlank, TrendUp, Microphone, Translate
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import { LOGO_URL } from '../config/branding';
import { trackConversion } from '../utils/analytics';
import { useWorkerLang } from '../contexts/WorkerLanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRADES = [
  { id: 'electrical', label: 'Electrical', icon: Plug, color: 'bg-amber-500' },
  { id: 'plumbing', label: 'Plumbing', icon: Drop, color: 'bg-blue-500' },
  { id: 'hvac', label: 'HVAC', icon: Thermometer, color: 'bg-cyan-500' },
  { id: 'carpentry', label: 'Carpentry', icon: Hammer, color: 'bg-orange-600' },
  { id: 'roofing', label: 'Roofing', icon: HardHat, color: 'bg-red-600' },
  { id: 'painting', label: 'Painting', icon: PaintBrush, color: 'bg-purple-500' },
  { id: 'flooring', label: 'Flooring', icon: Wrench, color: 'bg-teal-500' },
  { id: 'concrete', label: 'Concrete', icon: Wrench, color: 'bg-neutral-600' },
  { id: 'welding', label: 'Welding', icon: Lightning, color: 'bg-[#10B981]' },
  { id: 'drywall', label: 'Drywall', icon: Wrench, color: 'bg-lime-600' },
  { id: 'landscaping', label: 'Landscaping', icon: TreeEvergreen, color: 'bg-green-600' },
  { id: 'general', label: 'General Labor', icon: Wrench, color: 'bg-[#2B4C6F]' },
];

const SKILLS_BY_TRADE = {
  electrical: ['Wiring', 'Panel Upgrades', 'Lighting Install', 'Code Inspection', 'EV Charger', 'Generator Install', 'Smart Home'],
  plumbing: ['Pipe Repair', 'Water Heater', 'Drain Cleaning', 'Fixture Install', 'Sewer Line', 'Gas Line', 'Backflow Testing'],
  hvac: ['AC Install', 'Furnace Repair', 'Duct Work', 'Mini-Split', 'Refrigeration', 'Ventilation', 'Heat Pump'],
  carpentry: ['Framing', 'Trim Work', 'Cabinet Install', 'Deck Building', 'Door/Window Install', 'Custom Furniture', 'Structural Repair'],
  roofing: ['Shingle Install', 'Flat Roof', 'Metal Roof', 'Leak Repair', 'Gutter Install', 'Skylight', 'Roof Inspection'],
  painting: ['Interior Paint', 'Exterior Paint', 'Staining', 'Wallpaper', 'Texture/Drywall', 'Pressure Washing', 'Color Consultation'],
  flooring: ['Hardwood Install', 'Tile Install', 'Carpet Install', 'Vinyl/LVP', 'Floor Refinish', 'Polished Concrete', 'Epoxy'],
  concrete: ['Pouring', 'Stamping', 'Staining', 'Foundation Repair', 'Retaining Walls', 'Sidewalks', 'Demolition'],
  welding: ['MIG Welding', 'TIG Welding', 'Stick Welding', 'Structural Steel', 'Pipe Welding', 'Fabrication', 'Aluminum'],
  drywall: ['Hanging', 'Taping', 'Finishing', 'Texture', 'Repair', 'Soundproofing', 'Popcorn Removal'],
  landscaping: ['Lawn Care', 'Irrigation', 'Hardscaping', 'Tree Service', 'Garden Design', 'Fencing', 'Grading'],
  general: ['Demolition', 'Cleanup', 'Moving', 'Assembly', 'Handyman', 'Maintenance', 'Loading/Unloading'],
};

const JOB_TYPES = ['Residential New Build', 'Residential Remodel', 'Commercial Build-out', 'Commercial Maintenance', 'Emergency Repair', 'Government/Municipal', 'Industrial'];

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

// Bilingual string map — keys must match exactly between languages
const STRINGS = {
  en: {
    open_jobs: 'Open Jobs',
    hiring: 'Hiring',
    step_of: (cur, total) => `Step ${cur} of ${total}`,
    s_trade: 'Your Trade',
    s_exp: 'Experience',
    s_avail: 'Availability & Rate',
    s_loc: 'Location',
    s_acct: 'Create Account',
    promo_pill: '12 Languages · Free for Workers',
    promo_title: 'Speak your language — instant translation.',
    promo_body: 'Talk to any contractor or foreman in your language — 12 languages including Spanish, Portuguese, Polish, Mandarin & more. Tap the mic, speak, and hear the translation back. Construction terms stay accurate (varilla ↔ rebar, tablaroca ↔ drywall, andamio ↔ scaffold). Yours the moment you sign up.',
    step1_h: "What's your primary trade?",
    step1_sub: 'Select your main expertise. You can add more skills below.',
    add_skills: 'Add your skills',
    step2_h: 'Your experience',
    step2_sub: 'Tell us about your background so we can match you better.',
    years_exp: 'Years of Experience',
    certs: 'Certifications / Licenses',
    cert_placeholder: 'e.g. Licensed Electrician, OSHA 30',
    add: 'Add',
    past_work: 'Describe your past work (optional)',
    past_work_placeholder: "Projects you've worked on, specialties, notable achievements...",
    step3_h: 'Availability & rate',
    step3_sub: "When can you work and what's your rate?",
    schedule: 'Work Schedule',
    full_time: 'Full-Time',
    full_time_sub: '40+ hrs/week',
    part_time: 'Part-Time',
    part_time_sub: '20-30 hrs/week',
    weekends: 'Weekends',
    weekends_sub: 'Sat & Sun',
    flexible: 'Flexible',
    flexible_sub: 'Varies',
    contract: 'Contract Only',
    contract_sub: 'Per project',
    rate_label: 'Hourly Rate Range ($)',
    rate_min_ph: 'Min (e.g. 25)',
    rate_max_ph: 'Max (e.g. 55)',
    pref_jobs: 'Preferred Job Types',
    step4_h: 'Where do you work?',
    step4_sub: "We'll match you with jobs in your service area.",
    city: 'City',
    state: 'State',
    select: 'Select',
    zip: 'ZIP Code',
    radius: (n) => `Travel Radius: ${n} miles`,
    miles: 'mi',
    step5_h: 'Become a member',
    step5_sub: 'Set up your account so you can apply for jobs and get hired faster.',
    full_name: 'Full Name',
    name_ph: 'John Smith',
    email_label: 'Email',
    email_help: '(this will be your username)',
    email_ph: 'john@example.com',
    phone: 'Phone',
    phone_ph: '(555) 123-4567',
    password: 'Password',
    password_help: '(min 6 chars)',
    confirm_password: 'Confirm Password',
    password_mismatch: "Passwords don't match",
    legal: "By joining, you agree to ConstructHelp's Terms of Service and Privacy Policy.",
    already_member: 'Already a member?',
    login: 'Log in',
    back: 'Back',
    continue: 'Continue',
    creating: 'Creating account...',
    create_join: 'Create Account & Join',
    faq_title: 'Frequently Asked Questions',
    browse_first: (n) => `Just browsing? See ${n} open jobs first`,
    faq: [
      { q: 'Is it free for workers?', a: 'Yes, 100% free. Contractors pay to access the platform.' },
      { q: 'How do I get matched with jobs?', a: 'We auto-match your skills and location to open jobs. Create a full account to apply directly.' },
      { q: 'Can I set my own rates?', a: 'Absolutely. You set your rate range and negotiate directly with contractors.' },
      { q: 'What trades are in highest demand?', a: 'Electrical, HVAC, and plumbing are consistently the highest-demand trades on our platform.' },
    ],
    success_h: "You're a Member!",
    success_sub: 'Your account is ready and your profile has been submitted to our network.',
    match_score: 'Match Score',
    grade: 'Grade',
    jobs_matched: 'Jobs Matched',
    redirecting: 'Redirecting you to your dashboard...',
    translator_unlocked_title: 'Voice Translator (12 Languages)',
    translator_unlocked_body: 'Talk to any contractor, in any language. Find the mic on the',
    dashboard_hero_banner: 'Dashboard hero banner',
    go_dashboard: 'Go to Dashboard',
    return_home: 'Return Home',
    unlocked: 'UNLOCKED',
    trades_map: {
      electrical: 'Electrical', plumbing: 'Plumbing', hvac: 'HVAC',
      carpentry: 'Carpentry', roofing: 'Roofing', painting: 'Painting',
      flooring: 'Flooring', concrete: 'Concrete', welding: 'Welding',
      drywall: 'Drywall', landscaping: 'Landscaping', general: 'General Labor',
    },
    job_types_map: {}, // English uses raw values
    err_submit: 'Failed to submit',
    err_title: 'Error',
  },
  es: {
    open_jobs: 'Trabajos disponibles',
    hiring: 'Contratando',
    step_of: (cur, total) => `Paso ${cur} de ${total}`,
    s_trade: 'Tu oficio',
    s_exp: 'Experiencia',
    s_avail: 'Disponibilidad y tarifa',
    s_loc: 'Ubicación',
    s_acct: 'Crear cuenta',
    promo_pill: '12 idiomas · Gratis para trabajadores',
    promo_title: 'Habla tu idioma — traducción instantánea.',
    promo_body: 'Habla con cualquier contratista o capataz en tu idioma — 12 idiomas incluyendo español, portugués, polaco, mandarín y más. Toca el micrófono, habla y escucha la traducción. Los términos de construcción se mantienen exactos (varilla ↔ rebar, tablaroca ↔ drywall, andamio ↔ scaffold). Tuyo desde el momento que te registras.',
    step1_h: '¿Cuál es tu oficio principal?',
    step1_sub: 'Selecciona tu especialidad. Puedes agregar más habilidades abajo.',
    add_skills: 'Agrega tus habilidades',
    step2_h: 'Tu experiencia',
    step2_sub: 'Cuéntanos sobre tu trayectoria para emparejarte mejor.',
    years_exp: 'Años de experiencia',
    certs: 'Certificaciones / Licencias',
    cert_placeholder: 'ej. Electricista con licencia, OSHA 30',
    add: 'Agregar',
    past_work: 'Describe tu trabajo previo (opcional)',
    past_work_placeholder: 'Proyectos en los que has trabajado, especialidades, logros destacados...',
    step3_h: 'Disponibilidad y tarifa',
    step3_sub: '¿Cuándo puedes trabajar y cuál es tu tarifa?',
    schedule: 'Horario de trabajo',
    full_time: 'Tiempo completo',
    full_time_sub: '40+ hrs/semana',
    part_time: 'Medio tiempo',
    part_time_sub: '20-30 hrs/semana',
    weekends: 'Fines de semana',
    weekends_sub: 'Sáb y Dom',
    flexible: 'Flexible',
    flexible_sub: 'Varía',
    contract: 'Solo por contrato',
    contract_sub: 'Por proyecto',
    rate_label: 'Rango de tarifa por hora ($)',
    rate_min_ph: 'Mín (ej. 25)',
    rate_max_ph: 'Máx (ej. 55)',
    pref_jobs: 'Tipos de trabajo preferidos',
    step4_h: '¿Dónde trabajas?',
    step4_sub: 'Te conectaremos con trabajos en tu zona de servicio.',
    city: 'Ciudad',
    state: 'Estado',
    select: 'Seleccionar',
    zip: 'Código postal',
    radius: (n) => `Radio de viaje: ${n} millas`,
    miles: 'mi',
    step5_h: 'Conviértete en miembro',
    step5_sub: 'Crea tu cuenta para aplicar a trabajos y ser contratado más rápido.',
    full_name: 'Nombre completo',
    name_ph: 'Juan Pérez',
    email_label: 'Correo electrónico',
    email_help: '(será tu nombre de usuario)',
    email_ph: 'juan@ejemplo.com',
    phone: 'Teléfono',
    phone_ph: '(555) 123-4567',
    password: 'Contraseña',
    password_help: '(mín. 6 caracteres)',
    confirm_password: 'Confirmar contraseña',
    password_mismatch: 'Las contraseñas no coinciden',
    legal: 'Al unirte, aceptas los Términos de Servicio y la Política de Privacidad de ConstructHelp.',
    already_member: '¿Ya eres miembro?',
    login: 'Iniciar sesión',
    back: 'Atrás',
    continue: 'Continuar',
    creating: 'Creando cuenta...',
    create_join: 'Crear cuenta y unirme',
    faq_title: 'Preguntas frecuentes',
    browse_first: (n) => `¿Solo mirando? Ve ${n} trabajos disponibles primero`,
    faq: [
      { q: '¿Es gratis para los trabajadores?', a: 'Sí, 100% gratis. Los contratistas pagan por usar la plataforma.' },
      { q: '¿Cómo me conectan con trabajos?', a: 'Emparejamos automáticamente tus habilidades y ubicación con trabajos abiertos. Crea una cuenta completa para aplicar directamente.' },
      { q: '¿Puedo poner mis propias tarifas?', a: 'Por supuesto. Tú estableces tu rango de tarifa y negocias directamente con los contratistas.' },
      { q: '¿Qué oficios tienen más demanda?', a: 'Electricidad, HVAC y plomería son los oficios con mayor demanda en nuestra plataforma.' },
    ],
    success_h: '¡Ya eres miembro!',
    success_sub: 'Tu cuenta está lista y tu perfil se ha enviado a nuestra red.',
    match_score: 'Puntaje',
    grade: 'Grado',
    jobs_matched: 'Trabajos emparejados',
    redirecting: 'Te estamos llevando a tu panel...',
    translator_unlocked_title: 'Traductor de Voz (12 idiomas)',
    translator_unlocked_body: 'Habla con cualquier contratista, en cualquier idioma. Encuentra el micrófono en el',
    dashboard_hero_banner: 'banner principal del panel',
    go_dashboard: 'Ir al panel',
    return_home: 'Volver al inicio',
    unlocked: 'DESBLOQUEADO',
    trades_map: {
      electrical: 'Electricidad', plumbing: 'Plomería', hvac: 'HVAC',
      carpentry: 'Carpintería', roofing: 'Techado', painting: 'Pintura',
      flooring: 'Pisos', concrete: 'Concreto', welding: 'Soldadura',
      drywall: 'Tablaroca', landscaping: 'Jardinería', general: 'Mano de obra general',
    },
    job_types_map: {
      'Residential New Build': 'Construcción residencial nueva',
      'Residential Remodel': 'Remodelación residencial',
      'Commercial Build-out': 'Construcción comercial',
      'Commercial Maintenance': 'Mantenimiento comercial',
      'Emergency Repair': 'Reparación de emergencia',
      'Government/Municipal': 'Gubernamental / Municipal',
      'Industrial': 'Industrial',
    },
    err_submit: 'No se pudo enviar',
    err_title: 'Error',
  },
  pt: {
    open_jobs: 'Vagas abertas',
    hiring: 'Contratando',
    step_of: (cur, total) => `Etapa ${cur} de ${total}`,
    s_trade: 'Seu ofício',
    s_exp: 'Experiência',
    s_avail: 'Disponibilidade e tarifa',
    s_loc: 'Localização',
    s_acct: 'Criar conta',
    promo_pill: '12 idiomas · Grátis para trabalhadores',
    promo_title: 'Fale seu idioma — tradução instantânea.',
    promo_body: 'Fale com qualquer empreiteiro ou mestre de obras no seu idioma — 12 idiomas incluindo português, espanhol, polonês, mandarim e mais. Toque no microfone, fale e ouça a tradução. Os termos de construção permanecem exatos (vergalhão ↔ rebar, placa de gesso ↔ drywall, andaime ↔ scaffold). Seu desde o momento do cadastro.',
    step1_h: 'Qual é o seu ofício principal?',
    step1_sub: 'Selecione sua especialidade. Você pode adicionar mais habilidades abaixo.',
    add_skills: 'Adicione suas habilidades',
    step2_h: 'Sua experiência',
    step2_sub: 'Conte-nos sobre sua trajetória para combinarmos melhor.',
    years_exp: 'Anos de experiência',
    certs: 'Certificações / Licenças',
    cert_placeholder: 'ex. Eletricista licenciado, OSHA 30',
    add: 'Adicionar',
    past_work: 'Descreva seus trabalhos anteriores (opcional)',
    past_work_placeholder: 'Projetos em que trabalhou, especialidades, conquistas notáveis...',
    step3_h: 'Disponibilidade e tarifa',
    step3_sub: 'Quando você pode trabalhar e qual é a sua tarifa?',
    schedule: 'Horário de trabalho',
    full_time: 'Tempo integral',
    full_time_sub: '40+ h/semana',
    part_time: 'Meio período',
    part_time_sub: '20-30 h/semana',
    weekends: 'Fins de semana',
    weekends_sub: 'Sáb e Dom',
    flexible: 'Flexível',
    flexible_sub: 'Varia',
    contract: 'Somente contrato',
    contract_sub: 'Por projeto',
    rate_label: 'Faixa de tarifa por hora ($)',
    rate_min_ph: 'Mín (ex. 25)',
    rate_max_ph: 'Máx (ex. 55)',
    pref_jobs: 'Tipos de trabalho preferidos',
    step4_h: 'Onde você trabalha?',
    step4_sub: 'Vamos conectá-lo a vagas na sua área de atuação.',
    city: 'Cidade',
    state: 'Estado',
    select: 'Selecionar',
    zip: 'CEP (ZIP)',
    radius: (n) => `Raio de deslocamento: ${n} milhas`,
    miles: 'mi',
    step5_h: 'Torne-se membro',
    step5_sub: 'Crie sua conta para se candidatar a vagas e ser contratado mais rápido.',
    full_name: 'Nome completo',
    name_ph: 'João Silva',
    email_label: 'E-mail',
    email_help: '(será seu nome de usuário)',
    email_ph: 'joao@exemplo.com',
    phone: 'Telefone',
    phone_ph: '(555) 123-4567',
    password: 'Senha',
    password_help: '(mín. 6 caracteres)',
    confirm_password: 'Confirmar senha',
    password_mismatch: 'As senhas não coincidem',
    legal: 'Ao se cadastrar, você concorda com os Termos de Serviço e a Política de Privacidade da ConstructHelp.',
    already_member: 'Já é membro?',
    login: 'Entrar',
    back: 'Voltar',
    continue: 'Continuar',
    creating: 'Criando conta...',
    create_join: 'Criar conta e entrar',
    faq_title: 'Perguntas frequentes',
    browse_first: (n) => `Só olhando? Veja ${n} vagas abertas primeiro`,
    faq: [
      { q: 'É grátis para trabalhadores?', a: 'Sim, 100% grátis. Os empreiteiros pagam para usar a plataforma.' },
      { q: 'Como sou conectado às vagas?', a: 'Combinamos automaticamente suas habilidades e localização com vagas abertas. Crie uma conta completa para se candidatar diretamente.' },
      { q: 'Posso definir minhas próprias tarifas?', a: 'Com certeza. Você define sua faixa de tarifa e negocia diretamente com os empreiteiros.' },
      { q: 'Quais ofícios têm mais demanda?', a: 'Elétrica, HVAC e encanamento são consistentemente os ofícios com maior demanda na nossa plataforma.' },
    ],
    success_h: 'Você é membro!',
    success_sub: 'Sua conta está pronta e seu perfil foi enviado à nossa rede.',
    match_score: 'Pontuação',
    grade: 'Nota',
    jobs_matched: 'Vagas compatíveis',
    redirecting: 'Redirecionando para o seu painel...',
    translator_unlocked_title: 'Tradutor de Voz (12 idiomas)',
    translator_unlocked_body: 'Fale com qualquer empreiteiro, em qualquer idioma. Encontre o microfone no',
    dashboard_hero_banner: 'banner principal do painel',
    go_dashboard: 'Ir para o painel',
    return_home: 'Voltar ao início',
    unlocked: 'DESBLOQUEADO',
    trades_map: {
      electrical: 'Elétrica', plumbing: 'Encanamento', hvac: 'HVAC',
      carpentry: 'Carpintaria', roofing: 'Telhados', painting: 'Pintura',
      flooring: 'Pisos', concrete: 'Concreto', welding: 'Soldagem',
      drywall: 'Drywall', landscaping: 'Paisagismo', general: 'Serviços gerais',
    },
    job_types_map: {
      'Residential New Build': 'Construção residencial nova',
      'Residential Remodel': 'Reforma residencial',
      'Commercial Build-out': 'Construção comercial',
      'Commercial Maintenance': 'Manutenção comercial',
      'Emergency Repair': 'Reparo de emergência',
      'Government/Municipal': 'Governamental / Municipal',
      'Industrial': 'Industrial',
    },
    err_submit: 'Falha ao enviar',
    err_title: 'Erro',
  },
  pl: {
    open_jobs: 'Otwarte oferty',
    hiring: 'Zatrudniamy',
    step_of: (cur, total) => `Krok ${cur} z ${total}`,
    s_trade: 'Twój fach',
    s_exp: 'Doświadczenie',
    s_avail: 'Dostępność i stawka',
    s_loc: 'Lokalizacja',
    s_acct: 'Utwórz konto',
    promo_pill: '12 języków · Bezpłatnie dla pracowników',
    promo_title: 'Mów w swoim języku — natychmiastowe tłumaczenie.',
    promo_body: 'Rozmawiaj z każdym wykonawcą lub brygadzistą w swoim języku — 12 języków, w tym polski, hiszpański, portugalski, mandaryński i inne. Dotknij mikrofonu, mów i usłysz tłumaczenie. Terminy budowlane pozostają dokładne (zbrojenie ↔ rebar, płyta g-k ↔ drywall, rusztowanie ↔ scaffold). Twoje od chwili rejestracji.',
    step1_h: 'Jaki jest Twój główny fach?',
    step1_sub: 'Wybierz swoją specjalizację. Więcej umiejętności możesz dodać poniżej.',
    add_skills: 'Dodaj swoje umiejętności',
    step2_h: 'Twoje doświadczenie',
    step2_sub: 'Opowiedz nam o swojej karierze, abyśmy mogli lepiej Cię dopasować.',
    years_exp: 'Lata doświadczenia',
    certs: 'Certyfikaty / Uprawnienia',
    cert_placeholder: 'np. Elektryk z uprawnieniami, OSHA 30',
    add: 'Dodaj',
    past_work: 'Opisz swoje dotychczasowe prace (opcjonalnie)',
    past_work_placeholder: 'Projekty, przy których pracowałeś, specjalizacje, ważne osiągnięcia...',
    step3_h: 'Dostępność i stawka',
    step3_sub: 'Kiedy możesz pracować i jaka jest Twoja stawka?',
    schedule: 'Grafik pracy',
    full_time: 'Pełny etat',
    full_time_sub: '40+ godz./tydz.',
    part_time: 'Niepełny etat',
    part_time_sub: '20-30 godz./tydz.',
    weekends: 'Weekendy',
    weekends_sub: 'Sob i Ndz',
    flexible: 'Elastycznie',
    flexible_sub: 'Różnie',
    contract: 'Tylko kontrakt',
    contract_sub: 'Na projekt',
    rate_label: 'Zakres stawki godzinowej ($)',
    rate_min_ph: 'Min (np. 25)',
    rate_max_ph: 'Maks (np. 55)',
    pref_jobs: 'Preferowane rodzaje pracy',
    step4_h: 'Gdzie pracujesz?',
    step4_sub: 'Dopasujemy Cię do ofert w Twojej okolicy.',
    city: 'Miasto',
    state: 'Stan',
    select: 'Wybierz',
    zip: 'Kod pocztowy (ZIP)',
    radius: (n) => `Promień dojazdu: ${n} mil`,
    miles: 'mi',
    step5_h: 'Zostań członkiem',
    step5_sub: 'Załóż konto, aby aplikować na oferty i szybciej znaleźć pracę.',
    full_name: 'Imię i nazwisko',
    name_ph: 'Jan Kowalski',
    email_label: 'E-mail',
    email_help: '(będzie Twoją nazwą użytkownika)',
    email_ph: 'jan@przyklad.com',
    phone: 'Telefon',
    phone_ph: '(555) 123-4567',
    password: 'Hasło',
    password_help: '(min. 6 znaków)',
    confirm_password: 'Potwierdź hasło',
    password_mismatch: 'Hasła się nie zgadzają',
    legal: 'Dołączając, akceptujesz Regulamin i Politykę Prywatności ConstructHelp.',
    already_member: 'Masz już konto?',
    login: 'Zaloguj się',
    back: 'Wstecz',
    continue: 'Dalej',
    creating: 'Tworzenie konta...',
    create_join: 'Utwórz konto i dołącz',
    faq_title: 'Najczęściej zadawane pytania',
    browse_first: (n) => `Tylko przeglądasz? Zobacz najpierw ${n} otwartych ofert`,
    faq: [
      { q: 'Czy to bezpłatne dla pracowników?', a: 'Tak, w 100% bezpłatne. Za korzystanie z platformy płacą wykonawcy.' },
      { q: 'Jak jestem dopasowywany do ofert?', a: 'Automatycznie dopasowujemy Twoje umiejętności i lokalizację do otwartych ofert. Załóż pełne konto, aby aplikować bezpośrednio.' },
      { q: 'Czy mogę ustalać własne stawki?', a: 'Oczywiście. Ty ustalasz zakres swojej stawki i negocjujesz bezpośrednio z wykonawcami.' },
      { q: 'Które fachy są najbardziej poszukiwane?', a: 'Elektryka, HVAC i hydraulika to niezmiennie najbardziej poszukiwane fachy na naszej platformie.' },
    ],
    success_h: 'Jesteś członkiem!',
    success_sub: 'Twoje konto jest gotowe, a profil został przesłany do naszej sieci.',
    match_score: 'Wynik dopasowania',
    grade: 'Ocena',
    jobs_matched: 'Dopasowane oferty',
    redirecting: 'Przekierowujemy do Twojego panelu...',
    translator_unlocked_title: 'Tłumacz głosowy (12 języków)',
    translator_unlocked_body: 'Rozmawiaj z każdym wykonawcą, w dowolnym języku. Znajdź mikrofon na',
    dashboard_hero_banner: 'głównym banerze panelu',
    go_dashboard: 'Przejdź do panelu',
    return_home: 'Wróć na stronę główną',
    unlocked: 'ODBLOKOWANE',
    trades_map: {
      electrical: 'Elektryka', plumbing: 'Hydraulika', hvac: 'HVAC',
      carpentry: 'Stolarka', roofing: 'Dekarstwo', painting: 'Malowanie',
      flooring: 'Podłogi', concrete: 'Beton', welding: 'Spawanie',
      drywall: 'Płyty g-k', landscaping: 'Ogrodnictwo', general: 'Prace ogólne',
    },
    job_types_map: {
      'Residential New Build': 'Nowa budowa mieszkaniowa',
      'Residential Remodel': 'Remont mieszkaniowy',
      'Commercial Build-out': 'Wykończenie komercyjne',
      'Commercial Maintenance': 'Konserwacja komercyjna',
      'Emergency Repair': 'Naprawa awaryjna',
      'Government/Municipal': 'Rządowe / Miejskie',
      'Industrial': 'Przemysłowe',
    },
    err_submit: 'Nie udało się wysłać',
    err_title: 'Błąd',
  },
};

export default function FindWork() {
  const navigate = useNavigate();
  // Use shared worker language context so the choice flows to the dashboard
  const { lang, setLang } = useWorkerLang();
  const t = STRINGS[lang] || STRINGS.en;
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState({ active_workers: 0, open_jobs: 0, contractors_hiring: 0 });

  const [form, setForm] = useState({
    primary_trade: '', skills: [], experience_years: 0, certifications: [],
    past_work_description: '', availability: 'full_time',
    hourly_rate_min: '', hourly_rate_max: '',
    preferred_job_types: [], city: '', state: '', zip_code: '',
    travel_radius: 25, name: '', email: '', phone: '',
    password: '', confirm_password: '',
  });
  const [certInput, setCertInput] = useState('');

  const totalSteps = 5;
  const progress = (currentStep / totalSteps) * 100;

  useEffect(() => {
    axios.get(`${API_URL}/api/worker-funnel/stats`).then(r => { if (r.data?.success) setStats(r.data.stats); }).catch(() => {});
  }, []);

  const update = (field, value) => setForm(p => ({ ...p, [field]: value }));
  const toggleSkill = (s) => setForm(p => ({ ...p, skills: p.skills.includes(s) ? p.skills.filter(x => x !== s) : [...p.skills, s] }));
  const toggleJobType = (j) => setForm(p => ({ ...p, preferred_job_types: p.preferred_job_types.includes(j) ? p.preferred_job_types.filter(x => x !== j) : [...p.preferred_job_types, j] }));
  const addCert = () => { if (certInput.trim() && !form.certifications.includes(certInput.trim())) { update('certifications', [...form.certifications, certInput.trim()]); setCertInput(''); } };

  const validate = () => {
    switch (currentStep) {
      case 1: return !!form.primary_trade;
      case 2: return form.experience_years >= 0;
      case 3: return !!form.availability;
      case 4: return !!form.city && !!form.state;
      case 5: return (
        !!form.name.trim() &&
        !!form.email.trim() && form.email.includes('@') &&
        form.password.length >= 6 &&
        form.password === form.confirm_password
      );
      default: return true;
    }
  };

  const nextStep = () => { if (validate() && currentStep < totalSteps) setCurrentStep(currentStep + 1); };
  const prevStep = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        hourly_rate_min: form.hourly_rate_min ? Number(form.hourly_rate_min) : null,
        hourly_rate_max: form.hourly_rate_max ? Number(form.hourly_rate_max) : null,
        worker_id: localStorage.getItem('userId') || null,
        preferred_language: lang,
      };
      delete payload.confirm_password;
      // Apply pending referral code if present
      const pendingRef = localStorage.getItem('pending_referral_code');
      if (pendingRef) payload.referral_code = pendingRef;
      const res = await axios.post(`${API_URL}/api/worker-funnel/submit`, payload);
      if (res.data?.success) {
        trackConversion('worker_signup');
        localStorage.removeItem('pending_referral_code');
        // Auto-login the worker
        if (res.data.token && res.data.user) {
          localStorage.setItem('token', res.data.token);
          localStorage.setItem('userType', res.data.user.user_type);
          localStorage.setItem('userId', res.data.user._id);
          localStorage.setItem('user', JSON.stringify(res.data.user));
        }
        setResult(res.data);
        setSubmitted(true);
        // Auto-redirect with full reload so navbar/AuthContext picks up the new session
        setTimeout(() => { window.location.href = '/worker-dashboard'; }, 2500);
      }
    } catch (err) {
      toast({ title: t.err_title, description: err.response?.data?.detail || t.err_submit, variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  if (submitted && result) {
    return (
      <div className="min-h-screen bg-neutral-900 flex items-center justify-center p-4" data-testid="funnel-success">
        <div className="max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-black shadow-[4px_4px_0px_#000]">
            <CheckCircle size={48} weight="bold" className="text-white" />
          </div>
          <h1 className="text-3xl font-black text-white mb-2">{t.success_h}</h1>
          <p className="text-neutral-400 mb-4">{t.success_sub}</p>
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-white/5 border border-white/10 p-4 rounded-lg">
              <p className="text-2xl font-black text-[#2B4C6F]">{result.score}</p>
              <p className="text-[10px] text-neutral-400 uppercase">{t.match_score}</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-4 rounded-lg">
              <p className="text-2xl font-black text-emerald-400">{result.grade}</p>
              <p className="text-[10px] text-neutral-400 uppercase">{t.grade}</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-4 rounded-lg">
              <p className="text-2xl font-black text-[#10B981]">{result.matched_jobs}</p>
              <p className="text-[10px] text-neutral-400 uppercase">{t.jobs_matched}</p>
            </div>
          </div>
          <p className="text-neutral-500 text-sm mb-6">{t.redirecting}</p>
          {/* Voice Translator shout-out */}
          <div
            className="mb-6 bg-gradient-to-br from-emerald-500/20 to-transparent border-2 border-emerald-500 rounded-lg p-4 text-left"
            data-testid="success-translator-callout"
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center border-2 border-black">
                <Microphone size={20} weight="fill" className="text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[9px] font-black uppercase tracking-widest bg-emerald-500 text-white px-1.5 py-0.5 rounded">NEW</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">{t.unlocked}</span>
                </div>
                <p className="font-black text-white text-sm">{t.translator_unlocked_title}</p>
                <p className="text-xs text-neutral-300 mt-1">{t.translator_unlocked_body} <span className="font-bold text-emerald-400">{t.dashboard_hero_banner}</span>.</p>
              </div>
            </div>
          </div>
          <div className="flex gap-3 justify-center">
            <button onClick={() => { window.location.href = '/worker-dashboard'; }} className="h-12 px-6 bg-[#10B981] hover:bg-[#059669] text-white font-bold rounded-lg border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all" data-testid="go-dashboard-btn">{t.go_dashboard}</button>
            <button onClick={() => navigate('/')} className="h-12 px-6 bg-white/10 hover:bg-white/20 text-white font-bold rounded-lg border border-white/20 transition-all" data-testid="return-home-btn">{t.return_home}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900" data-testid="find-work-funnel">
      {/* Header */}
      <header className="bg-neutral-900 border-b-4 border-[#10B981]">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img src={LOGO_URL} alt="ConstructHelp" className="h-10 w-auto" />
          </div>
          <div className="hidden md:flex items-center gap-4 text-xs text-neutral-400">
            <span className="flex items-center gap-1"><Briefcase size={14} weight="bold" className="text-[#10B981]" /> {stats.open_jobs} {t.open_jobs}</span>
            <span className="flex items-center gap-1"><User size={14} weight="bold" className="text-[#2B4C6F]" /> {stats.contractors_hiring} {t.hiring}</span>
          </div>
          {/* Language Toggle */}
          <div
            className="inline-flex items-center bg-neutral-800 border-2 border-emerald-500 rounded-lg p-0.5 shadow-[2px_2px_0px_#10b981]"
            role="group"
            aria-label="Language toggle"
            data-testid="lang-toggle"
          >
            <button
              onClick={() => setLang('en')}
              className={`px-2.5 h-8 text-[11px] font-black uppercase tracking-widest rounded transition-all ${lang === 'en' ? 'bg-emerald-500 text-white' : 'text-neutral-400 hover:text-white'}`}
              data-testid="lang-en-btn"
              aria-pressed={lang === 'en'}
            >
              EN
            </button>
            <button
              onClick={() => setLang('es')}
              className={`px-2.5 h-8 text-[11px] font-black uppercase tracking-widest rounded transition-all ${lang === 'es' ? 'bg-emerald-500 text-white' : 'text-neutral-400 hover:text-white'}`}
              data-testid="lang-es-btn"
              aria-pressed={lang === 'es'}
            >
              ES
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* NEW Feature Promo — Voice Translator EN ↔ ES (bilingual hook) */}
        <div
          className="mb-6 relative overflow-hidden bg-gradient-to-br from-emerald-700 to-teal-900 border-2 border-emerald-400 rounded-xl shadow-[4px_4px_0px_#0d9488]"
          data-testid="voice-translator-promo"
        >
          <div className="absolute top-0 right-0 bg-emerald-400 text-emerald-900 text-[10px] font-black uppercase tracking-widest px-3 py-1 border-b-2 border-l-2 border-black">
            NEW
          </div>
          <div className="p-5 flex items-start gap-4">
            <div className="flex-shrink-0 w-14 h-14 bg-emerald-400 rounded-lg flex items-center justify-center border-2 border-black shadow-[2px_2px_0px_#000]">
              <Microphone size={28} weight="fill" className="text-emerald-900" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Translate size={14} weight="fill" className="text-emerald-400" />
                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-300">{t.promo_pill}</span>
              </div>
              <h3 className="font-black text-white text-lg leading-tight mb-1">
                {t.promo_title}
              </h3>
              <p className="text-xs text-neutral-300 leading-relaxed">
                {t.promo_body}
              </p>
            </div>
          </div>
        </div>

        {/* Just browsing? Bridge to job listings */}
        <div className="mb-6 text-center">
          <button
            onClick={() => navigate('/browse-jobs')}
            className="inline-flex items-center gap-2 text-sm font-bold text-[#2B4C6F] hover:text-[#203A55] underline underline-offset-4 transition-colors"
            data-testid="browse-jobs-first-link"
          >
            <Briefcase size={16} weight="bold" />
            {t.browse_first(stats.open_jobs)}
            <ArrowRight size={14} weight="bold" />
          </button>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-neutral-500 dark:text-neutral-400">{t.step_of(currentStep, totalSteps)}</span>
            <span className="text-xs font-bold text-[#10B981]">
              {currentStep === 1 && t.s_trade}
              {currentStep === 2 && t.s_exp}
              {currentStep === 3 && t.s_avail}
              {currentStep === 4 && t.s_loc}
              {currentStep === 5 && t.s_acct}
            </span>
          </div>
          <div className="w-full h-2 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
            <div className="h-full bg-[#10B981] transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-3xl p-6 sm:p-8 shadow-sm">

          {/* Step 1: Trade & Skills */}
          {currentStep === 1 && (
            <div data-testid="step-trade">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900 dark:text-white mb-2 text-center">{t.step1_h}</h2>
              <p className="text-neutral-500 dark:text-neutral-400 text-base mb-8 text-center">{t.step1_sub}</p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {TRADES.map(trade => (
                  <button key={trade.id} onClick={() => update('primary_trade', trade.id)}
                    className={`flex items-center gap-3 p-4 rounded-2xl border-2 bg-white dark:bg-neutral-900 shadow-sm transition-all ${form.primary_trade === trade.id ? 'border-[#10B981] bg-[#10B981]/5 shadow-md' : 'border-neutral-200 dark:border-neutral-600 hover:border-[#10B981]/40'}`}
                    data-testid={`trade-${trade.id}`}>
                    <div className={`w-10 h-10 ${trade.color} rounded-xl flex items-center justify-center`}>
                      <trade.icon size={20} weight="bold" className="text-white" />
                    </div>
                    <span className="font-bold text-sm text-neutral-900 dark:text-white">{t.trades_map[trade.id] || trade.label}</span>
                  </button>
                ))}
              </div>
              {form.primary_trade && (
                <>
                  <p className="text-xs font-bold uppercase text-neutral-500 mb-2">{t.add_skills}</p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(SKILLS_BY_TRADE[form.primary_trade] || []).map(s => (
                      <button key={s} onClick={() => toggleSkill(s)}
                        className={`px-3 py-1.5 text-xs font-bold rounded-full border-2 transition-all ${form.skills.includes(s) ? 'bg-[#10B981] text-white border-[#10B981]' : 'bg-white dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 border-neutral-300 dark:border-neutral-600 hover:border-[#10B981]'}`}
                        data-testid={`skill-${s.replace(/\s/g, '-')}`}>
                        {form.skills.includes(s) ? <CheckCircle size={12} weight="bold" className="inline mr-1" /> : null}{s}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 2: Experience */}
          {currentStep === 2 && (
            <div data-testid="step-experience">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900 dark:text-white mb-2 text-center">{t.step2_h}</h2>
              <p className="text-neutral-500 dark:text-neutral-400 text-base mb-8 text-center">{t.step2_sub}</p>
              <div className="mb-5">
                <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">{t.years_exp}</label>
                <input type="number" value={form.experience_years} onChange={(e) => update('experience_years', Math.max(0, parseInt(e.target.value) || 0))} min="0" max="50"
                  className="w-full h-12 px-4 text-lg bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#10B981] text-neutral-900 dark:text-white font-bold" data-testid="experience-years" />
              </div>
              <div className="mb-5">
                <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">{t.certs}</label>
                <div className="flex gap-2 mb-2">
                  <input value={certInput} onChange={(e) => setCertInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCert(); } }}
                    className="flex-1 h-10 px-3 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#10B981] text-neutral-900 dark:text-white"
                    placeholder={t.cert_placeholder} data-testid="cert-input" />
                  <button onClick={addCert} className="h-10 px-4 bg-[#2B4C6F] text-white font-bold text-sm rounded-full">{t.add}</button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {form.certifications.map(c => (
                    <span key={c} className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-bold rounded-lg border border-emerald-300 dark:border-emerald-700">
                      <Certificate size={12} weight="bold" /> {c}
                      <button onClick={() => update('certifications', form.certifications.filter(x => x !== c))}><X size={10} weight="bold" /></button>
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">{t.past_work}</label>
                <textarea value={form.past_work_description} onChange={(e) => update('past_work_description', e.target.value)} rows={3}
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#10B981] text-neutral-900 dark:text-white resize-none"
                  placeholder={t.past_work_placeholder} data-testid="past-work-input" />
              </div>
            </div>
          )}

          {/* Step 3: Availability & Rate */}
          {currentStep === 3 && (
            <div data-testid="step-availability">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900 dark:text-white mb-2 text-center">{t.step3_h}</h2>
              <p className="text-neutral-500 dark:text-neutral-400 text-base mb-8 text-center">{t.step3_sub}</p>
              <div className="mb-5">
                <label className="block text-xs font-bold uppercase text-neutral-500 mb-2">{t.schedule}</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[
                    { id: 'full_time', label: t.full_time, sub: t.full_time_sub },
                    { id: 'part_time', label: t.part_time, sub: t.part_time_sub },
                    { id: 'weekends', label: t.weekends, sub: t.weekends_sub },
                    { id: 'flexible', label: t.flexible, sub: t.flexible_sub },
                    { id: 'contract', label: t.contract, sub: t.contract_sub },
                  ].map(a => (
                    <button key={a.id} onClick={() => update('availability', a.id)}
                      className={`p-4 rounded-2xl border-2 text-left bg-white dark:bg-neutral-900 shadow-sm transition-all ${form.availability === a.id ? 'border-[#10B981] bg-[#10B981]/5 shadow-md' : 'border-neutral-200 dark:border-neutral-600 hover:border-[#10B981]/40'}`}
                      data-testid={`avail-${a.id}`}>
                      <p className="font-bold text-sm text-neutral-900 dark:text-white">{a.label}</p>
                      <p className="text-[10px] text-neutral-500">{a.sub}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div className="mb-5">
                <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">{t.rate_label}</label>
                <div className="grid grid-cols-2 gap-3">
                  <input type="number" value={form.hourly_rate_min} onChange={(e) => update('hourly_rate_min', e.target.value)} placeholder={t.rate_min_ph}
                    className="h-11 px-3 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#10B981] text-neutral-900 dark:text-white" data-testid="rate-min" />
                  <input type="number" value={form.hourly_rate_max} onChange={(e) => update('hourly_rate_max', e.target.value)} placeholder={t.rate_max_ph}
                    className="h-11 px-3 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#10B981] text-neutral-900 dark:text-white" data-testid="rate-max" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-neutral-500 mb-2">{t.pref_jobs}</label>
                <div className="flex flex-wrap gap-2">
                  {JOB_TYPES.map(j => (
                    <button key={j} onClick={() => toggleJobType(j)}
                      className={`px-3 py-1.5 text-xs font-bold rounded-full border-2 transition-all ${form.preferred_job_types.includes(j) ? 'bg-[#2B4C6F] text-white border-[#2B4C6F]' : 'bg-white dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 border-neutral-300 dark:border-neutral-600'}`}
                      data-testid={`jobtype-${j.replace(/\s/g, '-')}`}>
                      {t.job_types_map[j] || j}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Location */}
          {currentStep === 4 && (
            <div data-testid="step-location">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900 dark:text-white mb-2 text-center">{t.step4_h}</h2>
              <p className="text-neutral-500 dark:text-neutral-400 text-base mb-8 text-center">{t.step4_sub}</p>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">{t.city} *</label>
                  <input value={form.city} onChange={(e) => update('city', e.target.value)}
                    className="w-full h-11 px-3 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#10B981] text-neutral-900 dark:text-white"
                    placeholder={{ es: 'ej. Austin', pt: 'ex. Austin', pl: 'np. Austin' }[lang] || 'e.g. Austin'} data-testid="city-input" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">{t.state} *</label>
                  <select value={form.state} onChange={(e) => update('state', e.target.value)}
                    className="w-full h-11 px-3 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#10B981] text-neutral-900 dark:text-white" data-testid="state-select">
                    <option value="">{t.select}</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">{t.zip}</label>
                <input value={form.zip_code} onChange={(e) => update('zip_code', e.target.value)}
                  className="w-full h-11 px-3 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#10B981] text-neutral-900 dark:text-white"
                  placeholder={{ es: 'ej. 78701', pt: 'ex. 78701', pl: 'np. 78701' }[lang] || 'e.g. 78701'} data-testid="zip-input" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">{t.radius(form.travel_radius)}</label>
                <input type="range" min="5" max="100" step="5" value={form.travel_radius} onChange={(e) => update('travel_radius', Number(e.target.value))}
                  className="w-full accent-[#10B981]" data-testid="radius-slider" />
                <div className="flex justify-between text-[10px] text-neutral-400"><span>5 {t.miles}</span><span>50 {t.miles}</span><span>100 {t.miles}</span></div>
              </div>
            </div>
          )}

          {/* Step 5: Contact + Account */}
          {currentStep === 5 && (
            <div data-testid="step-contact">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900 dark:text-white mb-2 text-center">{t.step5_h}</h2>
              <p className="text-neutral-500 dark:text-neutral-400 text-base mb-8 text-center">{t.step5_sub}</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">{t.full_name} *</label>
                  <input value={form.name} onChange={(e) => update('name', e.target.value)}
                    className="w-full h-12 px-4 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#10B981] text-neutral-900 dark:text-white"
                    placeholder={t.name_ph} data-testid="name-input" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">{t.email_label} * <span className="text-neutral-400 normal-case font-normal">{t.email_help}</span></label>
                  <input type="email" value={form.email} onChange={(e) => update('email', e.target.value)}
                    className="w-full h-12 px-4 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#10B981] text-neutral-900 dark:text-white"
                    placeholder={t.email_ph} data-testid="email-input" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">{t.phone}</label>
                  <input type="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)}
                    className="w-full h-12 px-4 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#10B981] text-neutral-900 dark:text-white"
                    placeholder={t.phone_ph} data-testid="phone-input" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t-2 border-dashed border-neutral-200 dark:border-neutral-700">
                  <div>
                    <label className="block text-xs font-bold uppercase text-neutral-500 mb-1 mt-2">{t.password} * <span className="text-neutral-400 normal-case font-normal">{t.password_help}</span></label>
                    <input type="password" value={form.password} onChange={(e) => update('password', e.target.value)}
                      className="w-full h-12 px-4 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#10B981] text-neutral-900 dark:text-white"
                      placeholder="••••••••" data-testid="password-input" autoComplete="new-password" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase text-neutral-500 mb-1 mt-2">{t.confirm_password} *</label>
                    <input type="password" value={form.confirm_password} onChange={(e) => update('confirm_password', e.target.value)}
                      className={`w-full h-12 px-4 text-sm bg-white dark:bg-neutral-900 border-2 rounded-xl focus:outline-none text-neutral-900 dark:text-white ${
                        form.confirm_password && form.password !== form.confirm_password
                          ? 'border-red-500'
                          : 'border-neutral-300 dark:border-neutral-600 focus:border-[#10B981]'
                      }`}
                      placeholder="••••••••" data-testid="confirm-password-input" autoComplete="new-password" />
                    {form.confirm_password && form.password !== form.confirm_password && (
                      <p className="text-[11px] text-red-500 mt-1 font-bold" data-testid="password-mismatch">{t.password_mismatch}</p>
                    )}
                  </div>
                </div>
                <p className="text-[11px] text-neutral-500 dark:text-neutral-400 pt-2">{t.legal} {t.already_member} <button type="button" onClick={() => navigate('/login')} className="text-[#10B981] font-bold hover:underline" data-testid="goto-login-link">{t.login}</button></p>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center gap-3 mt-10">
            {currentStep > 1 && (
              <button onClick={prevStep} className="flex-1 flex items-center justify-center gap-2 h-14 bg-[#10B981] hover:bg-[#059669] text-white font-bold text-base rounded-full shadow-sm active:scale-[0.98] transition-all" data-testid="prev-step-btn">
                <ArrowLeft size={20} weight="bold" /> {t.back}
              </button>
            )}
            {currentStep < totalSteps ? (
              <button onClick={nextStep} disabled={!validate()}
                className={`flex-1 flex items-center justify-center gap-2 h-14 font-bold text-base rounded-full active:scale-[0.98] transition-all ${validate() ? 'bg-[#10B981] hover:bg-[#059669] text-white shadow-sm' : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-400 dark:text-neutral-500 cursor-not-allowed'}`}
                data-testid="next-step-btn">
                {t.continue} <ArrowRight size={20} weight="bold" />
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={!validate() || submitting}
                className={`flex-1 flex items-center justify-center gap-2 h-14 font-bold text-base rounded-full active:scale-[0.98] transition-all ${validate() && !submitting ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm' : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-400 dark:text-neutral-500 cursor-not-allowed'}`}
                data-testid="submit-btn">
                {submitting ? <Clock size={20} weight="bold" className="animate-spin" /> : <CheckCircle size={20} weight="bold" />}
                {submitting ? t.creating : t.create_join}
              </button>
            )}
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-8 bg-neutral-100 dark:bg-neutral-800 border-2 border-neutral-300 dark:border-neutral-700 rounded-xl p-5">
          <h3 className="font-bold text-neutral-900 dark:text-white mb-3">{t.faq_title}</h3>
          {t.faq.map((faq, i) => (
            <details key={i} className="mb-2 group">
              <summary className="cursor-pointer font-bold text-sm text-neutral-700 dark:text-neutral-300 flex items-center gap-2 py-1">
                <CaretDown size={14} weight="bold" className="text-[#10B981] group-open:rotate-180 transition-transform" /> {faq.q}
              </summary>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 pl-6 pb-2">{faq.a}</p>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
}

